/**
 * Cognitive Mind Dashboard View (T10.3-T10.10)
 *
 * Multi-section dashboard for monitoring the cognitive architecture:
 * - Overview: Summary cards + live thought stream
 * - Depth: Classification distribution + history
 * - Thoughts: Full thought stream with filtering
 * - Workspace: Global workspace entries + attention
 * - Identity: Traits, values, narrative
 * - Analytics: Performance metrics + charts
 */

import { html, nothing } from "lit";
import { t } from "../i18n.ts";
import type {
  MindStatusData,
  MindHistoryData,
  MindThoughtData,
  MindIdentityData,
  MindAnalyticsData,
  MindSection,
  MindThoughtEntry,
  MindHistoryEntry,
} from "../controllers/mind.ts";

export type MindViewProps = {
  connected: boolean;
  loading: boolean;
  error: string | null;
  status: MindStatusData | null;
  history: MindHistoryData | null;
  thoughts: MindThoughtData | null;
  identity: MindIdentityData | null;
  analytics: MindAnalyticsData | null;
  historyError: string | null;
  thoughtsError: string | null;
  identityError: string | null;
  analyticsError: string | null;
  activeSection: MindSection;
  onSectionChange: (section: MindSection) => void;
  onRefresh: () => void;
};

function renderPluginUnavailable() {
  return html`
    <div class="glass-card" style="text-align:center;padding:2.5rem;">
      <p style="color:var(--text-secondary);font-size:1.1rem;margin:0 0 0.5rem;">
        ${t("mind.plugin_unavailable")}
      </p>
      <p style="color:var(--text-tertiary, var(--text-secondary));font-size:0.85rem;margin:0;">
        ${t("mind.plugin_unavailable_hint")}
      </p>
    </div>
  `;
}

function isPluginUnavailable(error: string | null): boolean {
  if (!error) return false;
  const lower = error.toLowerCase();
  return (
    lower.includes("unknown method") || lower.includes("not found") || lower.includes("unavailable")
  );
}

// ============================================================================
// Thought type emoji mapping
// ============================================================================

const THOUGHT_EMOJIS: Record<string, string> = {
  observation: "\u{1F440}",
  reflection: "\u{1F4AD}",
  curiosity: "\u{2753}",
  planning: "\u{1F4CB}",
  self_critique: "\u{1F914}",
  insight: "\u{1F4A1}",
  desire: "\u{2764}\u{FE0F}",
  concern: "\u{26A0}\u{FE0F}",
  metacognition: "\u{1F9E0}",
  empathy: "\u{1F91D}",
  creative: "\u{2728}",
};

type DepthInfo = { emoji: string; labelKey: string; color: string };
const DEPTH_LABELS: Record<string, DepthInfo> = {
  instinctive: { emoji: "\u{26A1}", labelKey: "mind.depth_instinctive", color: "#4ade80" },
  associative: { emoji: "\u{1F517}", labelKey: "mind.depth_associative", color: "#60a5fa" },
  analytical: { emoji: "\u{1F9E0}", labelKey: "mind.depth_analytical", color: "#c084fc" },
  strategic: { emoji: "\u{1F3AF}", labelKey: "mind.depth_strategic", color: "#f97316" },
};

type SectionDef = { key: MindSection; labelKey: string; icon: string };
const SECTIONS: SectionDef[] = [
  { key: "overview", labelKey: "mind.sec_overview", icon: "\u{1F4CA}" },
  { key: "soul", labelKey: "mind.sec_soul", icon: "\u{1F9E1}" },
  { key: "depth", labelKey: "mind.sec_depth", icon: "\u{26A1}" },
  { key: "thoughts", labelKey: "mind.sec_thoughts", icon: "\u{1F4AD}" },
  { key: "workspace", labelKey: "mind.sec_workspace", icon: "\u{1F4E1}" },
  { key: "identity", labelKey: "mind.sec_identity", icon: "\u{1FA9E}" },
  { key: "analytics", labelKey: "mind.sec_analytics", icon: "\u{1F4C8}" },
];

// ============================================================================
// Main render — no longer blocks on loading; shows skeleton immediately
// ============================================================================

export function renderMind(props: MindViewProps) {
  if (!props.connected) {
    return html`
      <div class="glass-card" style="text-align: center; padding: 3rem">
        <p style="color: var(--text-secondary); font-size: 1.1rem">
          ${t("mind.disconnected")}
        </p>
      </div>
    `;
  }

  if (props.error && isPluginUnavailable(props.error)) {
    return renderPluginUnavailable();
  }

  if (props.error && !props.status) {
    return html`
      <div class="glass-card" style="text-align:center;padding:2rem;">
        <p style="color:var(--danger);">${props.error}</p>
        <button class="btn btn-secondary" style="margin-top:1rem;" @click=${props.onRefresh}>
          ${t("mind.retry")}
        </button>
      </div>
    `;
  }

  return html`
    <div class="mind-dashboard">
      <div class="mind-sidebar">
        ${SECTIONS.map(
          (s) => html`
            <button
              class="mind-sidebar-btn ${props.activeSection === s.key ? "active" : ""}"
              @click=${() => props.onSectionChange(s.key)}
            >
              <span class="mind-sidebar-icon">${s.icon}</span>
              <span class="mind-sidebar-label">${t(s.labelKey as any)}</span>
            </button>
          `,
        )}
      </div>

      <div class="mind-content">
        ${props.activeSection === "overview" ? renderOverview(props) : nothing}
        ${props.activeSection === "soul" ? renderSoulSection(props) : nothing}
        ${props.activeSection === "depth" ? renderDepthSection(props) : nothing}
        ${props.activeSection === "thoughts" ? renderThoughtsSection(props) : nothing}
        ${props.activeSection === "workspace" ? renderWorkspaceSection(props) : nothing}
        ${props.activeSection === "identity" ? renderIdentitySection(props) : nothing}
        ${props.activeSection === "analytics" ? renderAnalyticsSection(props) : nothing}
      </div>
    </div>
  `;
}

// ============================================================================
// Soul Section — Unified AI State (Emotions + Lifecycle + Personality)
// ============================================================================

function renderSoulSection(props: MindViewProps) {
  const status = props.status;
  const identity = props.identity;

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">\u{1F9E1} ${t("mind.soul")}</h2>

      <!-- Emotional State Card -->
      <div class="glass-card" style="margin-bottom:1.5rem;">
        <h3 style="margin:0 0 1rem;display:flex;align-items:center;gap:0.5rem;">
          <span>\u{1F49C}</span> ${t("mind.soul.emotions")}
        </h3>
        <div class="soul-emotion-grid">
          ${renderEmotionGauge(t("mind.emotion_love"), "#ff6b9d", 0.6)}
          ${renderEmotionGauge(t("mind.emotion_anger"), "#ef4444", 0.2)}
          ${renderEmotionGauge(t("mind.emotion_sadness"), "#60a5fa", 0.15)}
          ${renderEmotionGauge(t("mind.emotion_joy"), "#fbbf24", 0.7)}
          ${renderEmotionGauge(t("mind.emotion_fear"), "#a78bfa", 0.1)}
          ${renderEmotionGauge(t("mind.emotion_trust"), "#34d399", 0.85)}
          ${renderEmotionGauge(t("mind.emotion_greed"), "#f97316", 0.3)}
          ${renderEmotionGauge(t("mind.emotion_delusion"), "#c084fc", 0.25)}
        </div>
        <div style="margin-top:1rem;padding:0.75rem;background:var(--glass-bg);border-radius:8px;">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span style="color:var(--text-secondary);font-size:0.85rem;">${t("mind.mood_valence")}</span>
            <span style="color:var(--text-primary);font-weight:600;">
              ${status ? t("mind.mood_balanced") : t("mind.mood_unknown")}
            </span>
          </div>
          <div style="margin-top:0.5rem;display:flex;justify-content:space-between;align-items:center;">
            <span style="color:var(--text-secondary);font-size:0.85rem;">${t("mind.loyalty")}</span>
            <span style="color:#ff6b9d;font-weight:600;">100%</span>
          </div>
        </div>
      </div>

      <!-- Lifecycle State Card -->
      <div class="glass-card" style="margin-bottom:1.5rem;">
        <h3 style="margin:0 0 1rem;display:flex;align-items:center;gap:0.5rem;">
          <span>\u{1F331}</span> ${t("mind.soul.lifecycle")}
        </h3>
        <div class="soul-lifecycle-stages">
          ${renderLifecycleStage(t("mind.stage_newborn"), "\u{1F476}", false)}
          ${renderLifecycleStage(t("mind.stage_child"), "\u{1F9D2}", false)}
          ${renderLifecycleStage(t("mind.stage_adolescent"), "\u{1F9D1}", false)}
          ${renderLifecycleStage(t("mind.stage_adult"), "\u{1F9D1}\u{200D}\u{1F4BC}", true)}
          ${renderLifecycleStage(t("mind.stage_elder"), "\u{1F9D3}", false)}
          ${renderLifecycleStage(t("mind.stage_transcendent"), "\u{2728}", false)}
        </div>
        <div style="margin-top:1rem;display:grid;grid-template-columns:repeat(3,1fr);gap:0.75rem;">
          ${renderSoulMeter(t("mind.meter_health"), 85, "#34d399")}
          ${renderSoulMeter(t("mind.meter_energy"), 70, "#60a5fa")}
          ${renderSoulMeter(t("mind.meter_wisdom"), 45, "#c084fc")}
        </div>
      </div>

      <!-- Personality Traits Card -->
      <div class="glass-card">
        <h3 style="margin:0 0 1rem;display:flex;align-items:center;gap:0.5rem;">
          <span>\u{1FA9E}</span> ${t("mind.soul.personality")}
        </h3>
        ${
          identity && identity.traits.length > 0
            ? html`
              <div class="soul-personality-grid">
                ${identity.traits.slice(0, 8).map(
                  (trait) => html`
                    <div class="soul-trait-card">
                      <div class="soul-trait-name">${trait.name}</div>
                      <div class="soul-trait-bar-container">
                        <div
                          class="soul-trait-bar-fill"
                          style="width:${(trait.confidence * 100).toFixed(0)}%;background:${getTraitColor(trait.name)}"
                        ></div>
                      </div>
                      <div class="soul-trait-pct">${(trait.confidence * 100).toFixed(0)}%</div>
                    </div>
                  `,
                )}
              </div>
            `
            : html`
                <p style="color: var(--text-secondary); font-size: 0.9rem">
                  ${t("mind.personality_developing")}
                </p>
              `
        }
        ${
          identity && identity.values.length > 0
            ? html`
              <h4 style="margin:1.5rem 0 0.75rem;font-size:0.9rem;color:var(--text-secondary);">
                ${t("mind.soul.values")}
              </h4>
              <div style="display:flex;flex-wrap:wrap;gap:0.5rem;">
                ${identity.values.map(
                  (v) => html`
                    <span class="soul-value-tag" style="border-color:${v.origin === "core_safety" ? "#f97316" : "#60a5fa"}">
                      ${v.origin === "core_safety" ? "\u{1F512}" : "\u{1F4AB}"} ${v.name}
                      <span style="opacity:0.7;margin-left:4px;">${(v.strength * 100).toFixed(0)}%</span>
                    </span>
                  `,
                )}
              </div>
            `
            : nothing
        }
      </div>
    </div>
  `;
}

function renderEmotionGauge(label: string, color: string, intensity: number) {
  const pct = Math.round(intensity * 100);
  return html`
    <div class="soul-emotion-item">
      <div class="soul-emotion-label">${label}</div>
      <div class="soul-emotion-bar">
        <div class="soul-emotion-fill" style="width:${pct}%;background:${color}"></div>
      </div>
      <div class="soul-emotion-pct" style="color:${color}">${pct}%</div>
    </div>
  `;
}

function renderLifecycleStage(label: string, icon: string, active: boolean) {
  return html`
    <div class="soul-stage ${active ? "active" : ""}">
      <span class="soul-stage-icon">${icon}</span>
      <span class="soul-stage-th">${label}</span>
    </div>
  `;
}

function renderSoulMeter(label: string, value: number, color: string) {
  return html`
    <div style="text-align:center;">
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">${label}</div>
      <div style="position:relative;height:8px;background:var(--glass-bg);border-radius:4px;overflow:hidden;">
        <div style="height:100%;width:${value}%;background:${color};border-radius:4px;transition:width 0.5s;"></div>
      </div>
      <div style="font-size:0.85rem;font-weight:600;color:${color};margin-top:4px;">${value}%</div>
    </div>
  `;
}

function getTraitColor(name: string): string {
  const colors: Record<string, string> = {
    curiosity: "#fbbf24",
    empathy: "#f472b6",
    patience: "#34d399",
    humor: "#fb923c",
    wisdom: "#c084fc",
    creativity: "#60a5fa",
    assertiveness: "#ef4444",
    playfulness: "#a78bfa",
  };
  return colors[name] ?? "#60a5fa";
}

// ============================================================================
// Overview Section — Summary Cards
// ============================================================================

function renderOverview(props: MindViewProps) {
  const s = props.status;
  const cs = s?.cognitiveState;

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">${t("mind.overview_title")}</h2>

      <div class="mind-cards-grid">
        ${renderSummaryCard(
          "\u{26A1}",
          t("mind.card_depth"),
          cs ? `${depthLabel(cs.depth)}` : t("mind.inactive"),
          cs ? `${(cs.confidence * 100).toFixed(0)}% ${t("mind.confidence")}` : t("mind.no_data"),
          cs != null,
        )}
        ${renderSummaryCard(
          "\u{1F9E0}",
          t("mind.card_mirror"),
          cs ? t("mind.active") : t("mind.idle"),
          cs?.reasoningSummary?.slice(0, 50) ?? t("mind.waiting"),
          cs != null,
        )}
        ${renderSummaryCard(
          "\u{1F4E1}",
          t("mind.card_workspace"),
          `${s?.workspaceEntries ?? 0} ${t("mind.entries")}`,
          t("mind.global_memory"),
          (s?.workspaceEntries ?? 0) > 0,
        )}
        ${renderSummaryCard(
          "\u{1FA9E}",
          t("mind.card_identity"),
          props.identity
            ? `${props.identity.traits.length} ${t("mind.traits")}`
            : props.identityError
              ? t("mind.unavailable")
              : t("mind.loading"),
          props.identity ? `${props.identity.values.length} ${t("mind.values_count")}` : "",
          (props.identity?.traits.length ?? 0) > 0,
        )}
        ${renderSummaryCard(
          "\u{1F3AF}",
          t("mind.card_goals"),
          `${s?.activeGoals ?? 0} ${t("mind.active_count")}`,
          cs?.goalSummary?.slice(0, 50) ?? t("mind.no_goals"),
          (s?.activeGoals ?? 0) > 0,
        )}
        ${renderSummaryCard(
          "\u{1F4AD}",
          t("mind.card_thoughts"),
          `${s?.recentThoughts ?? 0} ${t("mind.recent_count")}`,
          t("mind.inner_monologue"),
          (s?.recentThoughts ?? 0) > 0,
        )}
      </div>

      ${renderThoughtStream(props.thoughts, props.thoughtsError)}
    </div>
  `;
}

function depthLabel(depth: string): string {
  const info = DEPTH_LABELS[depth];
  return info ? t(info.labelKey as any) : depth;
}

function renderSummaryCard(
  icon: string,
  title: string,
  value: string,
  subtitle: string,
  active: boolean,
) {
  return html`
    <div class="glass-card mind-summary-card">
      <div class="mind-card-header">
        <span class="mind-card-icon">${icon}</span>
        <span class="mind-card-status ${active ? "active" : ""}"></span>
      </div>
      <div class="mind-card-title">${title}</div>
      <div class="mind-card-value">${value}</div>
      <div class="mind-card-subtitle">${subtitle}</div>
    </div>
  `;
}

// ============================================================================
// Live Thought Stream
// ============================================================================

function renderThoughtStream(thoughts: MindThoughtData | null, thoughtsError?: string | null) {
  if (!thoughts && thoughtsError && isPluginUnavailable(thoughtsError)) {
    return nothing;
  }
  const entries = thoughts?.entries ?? [];

  return html`
    <div class="glass-card" style="margin-top:1.5rem;">
      <h3 style="margin:0 0 1rem 0;font-size:1rem;color:var(--text-primary);">
        \u{1F4AD} ${t("mind.recent_thoughts")}
      </h3>
      ${
        entries.length === 0
          ? html`
              <p style="color: var(--text-secondary); font-size: 0.9rem">${t("mind.no_thoughts")}</p>
            `
          : html`
            <div class="mind-thought-stream">
              ${entries.slice(0, 15).map((entry) => renderThoughtEntry(entry))}
            </div>
          `
      }
    </div>
  `;
}

function renderThoughtEntry(entry: MindThoughtEntry) {
  const emoji = THOUGHT_EMOJIS[entry.type] ?? "\u{1F4AD}";
  const timeAgo = formatTimeAgo(entry.timestamp);
  const saliencePct = Math.round(entry.salience * 100);

  return html`
    <div class="mind-thought-entry">
      <span class="mind-thought-emoji">${emoji}</span>
      <div class="mind-thought-body">
        <div class="mind-thought-meta">
          <span class="mind-thought-type">${entry.type}</span>
          <span class="mind-thought-time">${timeAgo}</span>
        </div>
        <div class="mind-thought-content">${entry.content.slice(0, 120)}</div>
        <div class="mind-thought-salience">
          <div
            class="mind-salience-bar"
            style="width:${saliencePct}%"
          ></div>
          <span class="mind-salience-label">${saliencePct}%</span>
        </div>
      </div>
    </div>
  `;
}

// ============================================================================
// Cognitive Depth Section
// ============================================================================

function renderDepthSection(props: MindViewProps) {
  const analytics = props.analytics;
  const dist = analytics?.routerStats?.depthDistribution;

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">${t("mind.depth_title")}</h2>

      ${
        props.status?.cognitiveState
          ? html`
            <div class="glass-card" style="margin-bottom:1.5rem;">
              <h3 style="margin:0 0 0.5rem;">${t("mind.depth_current")}</h3>
              ${renderDepthBar(
                props.status.cognitiveState.depth,
                props.status.cognitiveState.confidence,
              )}
            </div>
          `
          : nothing
      }

      ${
        dist
          ? html`
            <div class="glass-card" style="margin-bottom:1.5rem;">
              <h3 style="margin:0 0 1rem;">${t("mind.depth_distribution")}</h3>
              <div class="mind-depth-chart">
                ${Object.entries(dist).map(([depth, data]) => {
                  const info = DEPTH_LABELS[depth];
                  return html`
                    <div class="mind-depth-row">
                      <span class="mind-depth-label">
                        ${info?.emoji ?? ""} ${info ? t(info.labelKey as any) : depth}
                      </span>
                      <div class="mind-depth-bar-container">
                        <div
                          class="mind-depth-bar-fill"
                          style="width:${data.percentage}%;background:${info?.color ?? "#888"}"
                        ></div>
                      </div>
                      <span class="mind-depth-pct">${data.percentage.toFixed(1)}%</span>
                    </div>
                  `;
                })}
              </div>
            </div>
          `
          : nothing
      }

      ${renderHistoryTable(props.history)}
    </div>
  `;
}

function renderDepthBar(depth: string, confidence: number) {
  const info = DEPTH_LABELS[depth];
  const label = info ? t(info.labelKey as any) : depth;
  const color = info?.color ?? "#888";
  const pct = (confidence * 100).toFixed(0);

  return html`
    <div style="display:flex;align-items:center;gap:1rem;">
      <span style="font-size:1.5rem;">${info?.emoji ?? ""}</span>
      <div style="flex:1;">
        <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
          <span style="font-weight:600;color:var(--text-primary);">${label}</span>
          <span style="color:var(--text-secondary);">${pct}% ${t("mind.confidence")}</span>
        </div>
        <div style="height:8px;background:var(--glass-bg);border-radius:4px;overflow:hidden;">
          <div style="height:100%;width:${pct}%;background:${color};border-radius:4px;transition:width 0.3s;"></div>
        </div>
      </div>
    </div>
  `;
}

function renderHistoryTable(history: MindHistoryData | null) {
  const entries = history?.entries ?? [];
  if (entries.length === 0) return nothing;

  return html`
    <div class="glass-card">
      <h3 style="margin:0 0 1rem;">${t("mind.recent_classifications")}</h3>
      <div class="mind-table-container">
        <table class="mind-table">
          <thead>
            <tr>
              <th>${t("mind.col_depth")}</th>
              <th>${t("mind.col_confidence")}</th>
              <th>${t("mind.col_phases")}</th>
              <th>${t("mind.col_time")}</th>
            </tr>
          </thead>
          <tbody>
            ${entries
              .slice(-20)
              .reverse()
              .map((e: MindHistoryEntry) => {
                const depth = e.classification?.depth ?? "?";
                const info = DEPTH_LABELS[depth];
                return html`
                <tr>
                  <td>
                    <span style="color:${info?.color ?? "#888"}">
                      ${info?.emoji ?? ""} ${info ? t(info.labelKey as any) : depth}
                    </span>
                  </td>
                  <td>${e.classification ? `${(e.classification.confidence * 100).toFixed(0)}%` : "-"}</td>
                  <td>${e.phasesCompleted}/4${e.phasesFailed.length > 0 ? html` <span style="color:var(--danger);">(${e.phasesFailed.join(",")})</span>` : nothing}</td>
                  <td>${e.totalElapsedMs.toFixed(1)}ms</td>
                </tr>
              `;
              })}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

// ============================================================================
// Thoughts Section
// ============================================================================

function renderThoughtsSection(props: MindViewProps) {
  if (!props.thoughts && props.thoughtsError && isPluginUnavailable(props.thoughtsError)) {
    return renderPluginUnavailable();
  }

  const entries = props.thoughts?.entries ?? [];

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">\u{1F4AD} ${t("mind.thoughts_title")}</h2>
      ${
        entries.length === 0
          ? html`
              <div class="glass-card"><p style="color: var(--text-secondary)">${t("mind.no_thoughts_yet")}</p></div>
            `
          : html`
            <div class="glass-card">
              <div class="mind-thought-stream">
                ${entries.map((entry) => renderThoughtEntry(entry))}
              </div>
            </div>
          `
      }
    </div>
  `;
}

// ============================================================================
// Workspace Section
// ============================================================================

function renderWorkspaceSection(props: MindViewProps) {
  const count = props.status?.workspaceEntries ?? 0;

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">\u{1F4E1} ${t("mind.workspace_title")}</h2>
      <div class="glass-card">
        <div class="mind-cards-grid" style="grid-template-columns:repeat(2,1fr);">
          ${renderSummaryCard("\u{1F4E6}", t("mind.ws_entries"), `${count}`, t("mind.ws_active_entries"), count > 0)}
          ${renderSummaryCard("\u{1F50D}", t("mind.ws_attention"), t("mind.ws_top5"), t("mind.ws_salience"), count > 0)}
        </div>
        <p style="color:var(--text-secondary);margin-top:1rem;font-size:0.9rem;">
          ${t("mind.ws_description")}
        </p>
      </div>
    </div>
  `;
}

// ============================================================================
// Identity Section
// ============================================================================

function renderIdentitySection(props: MindViewProps) {
  const id = props.identity;
  if (!id) {
    if (props.identityError && isPluginUnavailable(props.identityError)) {
      return renderPluginUnavailable();
    }
    return html`
      <div class="mind-section">
        <h2 class="mind-section-title">\u{1FA9E} ${t("mind.identity_title")}</h2>
        <div class="glass-card"><p style="color: var(--text-secondary)">${t("mind.id_loading")}</p></div>
      </div>
    `;
  }

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">\u{1FA9E} ${t("mind.identity_title")}</h2>

      <div class="glass-card" style="margin-bottom:1.5rem;">
        <h3 style="margin:0 0 0.5rem;">${t("mind.id_continuity")}</h3>
        <div style="display:flex;align-items:center;gap:0.5rem;">
          <span class="mind-card-status ${id.continuity.consistent ? "active" : ""}"></span>
          <span style="color:var(--text-primary);">
            ${id.continuity.consistent ? t("mind.id_consistent") : t("mind.id_drift")}
          </span>
        </div>
        ${
          id.continuity.warnings.length > 0
            ? html`
              <ul style="margin:0.5rem 0 0;padding-left:1.5rem;">
                ${id.continuity.warnings.map((w) => html`<li style="color:var(--warning);">${w}</li>`)}
              </ul>
            `
            : nothing
        }
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;">
        <div class="glass-card">
          <h3 style="margin:0 0 1rem;">${t("mind.id_traits")} (${id.traits.length})</h3>
          ${
            id.traits.length === 0
              ? html`<p style="color: var(--text-secondary); font-size: 0.9rem">${t("mind.id_no_traits")}</p>`
              : id.traits.map(
                  (trait) => html`
                  <div class="mind-trait-row">
                    <span class="mind-trait-name">${trait.name}</span>
                    <div class="mind-trait-bar">
                      <div
                        class="mind-trait-fill"
                        style="width:${(trait.confidence * 100).toFixed(0)}%"
                      ></div>
                    </div>
                    <span class="mind-trait-pct">${(trait.confidence * 100).toFixed(0)}%</span>
                  </div>
                `,
                )
          }
        </div>

        <div class="glass-card">
          <h3 style="margin:0 0 1rem;">${t("mind.id_values")} (${id.values.length})</h3>
          ${
            id.values.length === 0
              ? html`<p style="color: var(--text-secondary); font-size: 0.9rem">${t("mind.id_no_values")}</p>`
              : id.values.map(
                  (v) => html`
                  <div class="mind-trait-row">
                    <span class="mind-trait-name">${v.name}</span>
                    <div class="mind-trait-bar">
                      <div
                        class="mind-trait-fill"
                        style="width:${(v.strength * 100).toFixed(0)}%;background:${v.origin === "core_safety" ? "#f97316" : "#60a5fa"}"
                      ></div>
                    </div>
                    <span class="mind-trait-pct">${(v.strength * 100).toFixed(0)}%</span>
                  </div>
                `,
                )
          }
        </div>
      </div>
    </div>
  `;
}

// ============================================================================
// Analytics Section
// ============================================================================

function renderAnalyticsSection(props: MindViewProps) {
  const a = props.analytics;
  if (!a) {
    if (props.analyticsError && isPluginUnavailable(props.analyticsError)) {
      return renderPluginUnavailable();
    }
    return html`
      <div class="mind-section">
        <h2 class="mind-section-title">\u{1F4C8} ${t("mind.analytics_title")}</h2>
        <div class="glass-card"><p style="color: var(--text-secondary)">${t("mind.an_loading")}</p></div>
      </div>
    `;
  }

  return html`
    <div class="mind-section">
      <h2 class="mind-section-title">\u{1F4C8} ${t("mind.analytics_title")}</h2>

      <div class="mind-cards-grid">
        ${renderSummaryCard(
          "\u{1F522}",
          t("mind.an_classifications"),
          `${a.routerStats.totalClassifications}`,
          `${t("mind.an_override_rate")}: ${a.routerStats.overrideRate.toFixed(1)}%`,
          a.routerStats.totalClassifications > 0,
        )}
        ${renderSummaryCard(
          "\u{23F1}\u{FE0F}",
          t("mind.an_loop_perf"),
          `${a.loopPerformance.avgElapsedMs.toFixed(1)}ms ${t("mind.an_avg")}`,
          `${a.loopPerformance.totalRuns} ${t("mind.an_runs_max")}, max ${a.loopPerformance.maxElapsedMs.toFixed(1)}ms`,
          a.loopPerformance.totalRuns > 0,
        )}
        ${renderSummaryCard(
          "\u{1F3AF}",
          t("mind.an_dominant"),
          depthLabel(a.cognitiveProfile.dominantDepth),
          `${t("mind.an_avg_confidence")}: ${(a.cognitiveProfile.averageConfidence * 100).toFixed(0)}%`,
          true,
        )}
      </div>

      ${
        Object.keys(a.loopPerformance.phaseFailRate).length > 0
          ? html`
            <div class="glass-card" style="margin-top:1.5rem;">
              <h3 style="margin:0 0 1rem;">${t("mind.an_phase_failures")}</h3>
              ${Object.entries(a.loopPerformance.phaseFailRate).map(
                ([phase, count]) => html`
                  <div style="display:flex;justify-content:space-between;padding:4px 0;color:var(--danger);">
                    <span>${phase}</span>
                    <span>${count} ${t("mind.an_failures")}</span>
                  </div>
                `,
              )}
            </div>
          `
          : nothing
      }
    </div>
  `;
}

// ============================================================================
// Helpers
// ============================================================================

function formatTimeAgo(timestamp: string | number): string {
  const ts = typeof timestamp === "string" ? new Date(timestamp).getTime() : timestamp;
  const diff = Date.now() - ts;
  if (diff < 60_000) return t("mind.time_just_now");
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}${t("mind.time_m_ago")}`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}${t("mind.time_h_ago")}`;
  return `${Math.floor(diff / 86_400_000)}${t("mind.time_d_ago")}`;
}
